import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { ProductLogo } from "./ProductLogo";

const CARDS = [
  {
    n: "01",
    title: "Start Here",
    body: "Understand how the system works and find the best place to begin.",
    cta: "Explore",
    href: "https://app.notion.com/p/b166284ba1e24549921e1dd6c06932e0",
  },
  {
    n: "02",
    title: "10 Learning Modules",
    body: "Build practical AI skills through prompting, judgment, and real work scenarios.",
    cta: "Explore Modules",
    href: "https://app.notion.com/p/ffb2c08576e44d4983d31355e967576d",
  },
  {
    n: "03",
    title: "400+ Prompt Examples",
    body: "Practical prompts for real work — ready to adapt and make your own.",
    cta: "Explore Prompts",
    href: "https://app.notion.com/p/4578f2b39e0f499b98723da44dfee5fd",
  },
  {
    n: "04",
    title: "Better Days with AI",
    body: "Turn what you learn into better everyday AI habits.",
    cta: "Explore Module 10",
    href: "https://app.notion.com/p/smartaicompanion/10-Better-Days-with-AI-3e1cc1085a75810d951cff5ab487faed",
  },
];

const FeatureCard = ({ card }) => {
  const inner = (
    <>
      <span className="font-mono text-[10px] uppercase tracking-eyebrow text-platinum">
        {card.n}
      </span>
      <h3 className="mt-3 text-[16px] font-bold tracking-tight text-ink">
        {card.title}
      </h3>
      <p className="mt-2 flex-1 text-[13.5px] leading-relaxed text-slatetx">
        {card.body}
      </p>
      {card.href && (
        <span className="mt-4 inline-flex items-center gap-1 text-[12.5px] font-semibold text-ink">
          {card.cta}
          <ArrowUpRight
            size={14}
            className="text-steel transition-transform duration-300 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-ink"
          />
        </span>
      )}
    </>
  );
  const cls =
    "group flex h-full flex-col rounded-[18px] border border-hairline bg-surface p-7 transition-[transform,box-shadow,border-color] duration-300 ease-out";
  if (!card.href) {
    return (
      <div data-testid={`smartai-card-${card.n}`} className={cls}>
        {inner}
      </div>
    );
  }
  return (
    <a
      href={card.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`smartai-card-${card.n}`}
      className={`${cls} hover:-translate-y-0.5 hover:border-steel/40 hover:card-shadow`}
    >
      {inner}
    </a>
  );
};

export const FeaturedProject = () => {
  return (
    <section id="featured-project" data-testid="featured-project" className="bg-canvas">
      <div className="mx-auto max-w-6xl px-6 py-12 md:py-16 lg:px-8">
        <Reveal>
          <Eyebrow>Featured Project</Eyebrow>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-5 flex items-center gap-4">
            <ProductLogo
              product={{
                id: "smart-ai-companion",
                name: "SMART AI Companion",
                logo: "/logos/smartai.png",
              }}
              rounded="rounded-[22%]"
              className="h-14 w-14 card-shadow"
            />
            <h2 className="font-bold tracking-tight text-ink text-[clamp(1.9rem,4vw,2.25rem)]">
              SMART AI Companion
            </h2>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mt-5 max-w-2xl text-[17px] font-medium leading-relaxed text-ink">
            Learn AI by using it on real work.
          </p>
          <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-slatetx">
            A practical learning system that combines guided modules, 400+
            prompt examples, and hands-on practice to help people use AI with
            more clarity and confidence.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            data-testid="smartai-value-summary"
            className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-slatetx"
          >
            <span>9 Learning Modules</span>
            <span className="h-3 w-px bg-hairline" aria-hidden="true" />
            <span>400+ Prompt Examples</span>
            <span className="h-3 w-px bg-hairline" aria-hidden="true" />
            <span>Better Days with AI</span>
          </div>
        </Reveal>

        {/* Four interactive cards */}
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CARDS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05} className="h-full">
              <FeatureCard card={c} />
            </Reveal>
          ))}
        </div>

        {/* Ecosystem visual */}
        <Reveal delay={0.1}>
          <div className="mx-auto mt-10 max-w-[800px]">
            <div
              data-testid="smartai-flow-visual"
              className="w-full overflow-hidden rounded-[20px] border border-hairline bg-surface p-2"
            >
              <img
                src="/smart-flow-v2.webp"
                alt="SMART AI Companion system map — Start Here, Understand, Personalize, Humanize, Prompt, Apply, Evaluate, with Prompt Vault, Troubleshoot, My AI Profile, Real-Life Workflows, My Lab, Glossary, and AI Usage Manifesto"
                loading="lazy"
                className="block h-auto w-full rounded-[14px] object-contain"
              />
            </div>
            <p className="mt-4 text-center text-[13px] leading-relaxed text-platinum">
              The system connects learning, prompting, practice, and reflection
              — so useful AI skills can build over time.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
